import { useEffect, useState } from 'react';
import { toast } from "react-hot-toast";
import AsignEmployee from './AsignEmployee';
import { deleteBatchEmployeeApi, getBatchEmployeesByBatchIdApi, updateBatchEmployeeApi, addBatchEmployeeApi } from '../../../../Api/BatchEmployee';
import SideModal from '../../../general/Sidemodal';
import BatchEmployeeForm from './BatchEmployeeForm';
import DeleteItem from '../../../../Trust/components/DeleteItem';
import { FaPlus } from "react-icons/fa";


type Props = {
  url: string,
  batchId: string,
};

type BatchEmployeeType = {
  id: string;
  batchId: string;
  employeeId: string;
  hourlyRate: number;
  startTime: string; // ISO time string, e.g. "HH:mm:ss"
  endtime: string;   // ISO time string, e.g. "HH:mm:ss"
  startDate: string; // ISO date string, e.g. "YYYY-MM-DD"
  endDate?: string;  // ISO date string, e.g. "YYYY-MM-DD"
  user: {
    id: string;
    name: string;
    email: string;
    mobile: string;
    gender: number | null;
    address: string;
    roleType: string;
  };
};

const BatchEmployee = ({ url, batchId }: Props) => {
  const [assignModal, setAssignModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [employees, setEmployees] = useState<BatchEmployeeType[]>([]);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editbatchemployeeId, setEditbatchemployeeId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string } | null>(null);

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [salaryReleaseModalOpen, setSalaryReleaseModalOpen] = useState(false);
  const [salaryReleaseEmployee, setSalaryReleaseEmployee] = useState<BatchEmployeeType | null>(null);
  const [salaryReleaseEndDate, setSalaryReleaseEndDate] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number, left: number } | null>(null);
  const [customSalary, setCustomSalary] = useState<string>("");


  const fetchEmployees = async () => {
    try {
      setLoader(true);
      const response = await getBatchEmployeesByBatchIdApi(
        url,
        batchId,
        searchTerm,
        currentPage,
        itemsPerPage
      );
      setEmployees(response.items || []);
      setTotalPages(Math.ceil((response.totalRecords || 0) / itemsPerPage));
    } catch (error) {
      toast.error("Failed to fetch employees.");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line
  }, [currentPage, searchTerm, assignModal, isFormModalOpen]);

  useEffect(() => {
    function handleClick() {
      setDropdownPosition(null);
    }
    if (dropdownPosition) {
      window.addEventListener('scroll', handleClick, true);
      window.addEventListener('mousedown', handleClick, true);
    }
    return () => {
      window.removeEventListener('scroll', handleClick, true);
      window.removeEventListener('mousedown', handleClick, true);
    };
  }, [dropdownPosition]);

  useEffect(() => {
    if (salaryReleaseEmployee) {
      const startDate = salaryReleaseEmployee.startDate;
      const endDate = salaryReleaseEndDate || salaryReleaseEmployee.endDate;
      if (endDate) {
        const days = calculateDays(startDate, endDate);
        const hours = calculateHours(salaryReleaseEmployee.startTime, salaryReleaseEmployee.endtime);
        const total = days * hours * salaryReleaseEmployee.hourlyRate;
        setCustomSalary(total.toFixed(2));
      } else {
        setCustomSalary("");
      }
    }
  }, [salaryReleaseEmployee, salaryReleaseEndDate]);
  
  // Search handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetchEmployees();
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleEditClick = (id: string) => {
    setEditbatchemployeeId(id);
    setIsFormModalOpen(true);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItem({ id });
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;

    try {
      await deleteBatchEmployeeApi(selectedItem.id, url);
      toast.success("Deleted successfully!");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to delete." + error);
    } finally {
      setIsModalOpen(false);
      setSelectedItem(null);
    }
  };

  const handleSalaryReleaseClick = (employee: BatchEmployeeType) => {
    setSalaryReleaseEmployee(employee);
    setSalaryReleaseEndDate(employee.endDate || null);
    setSalaryReleaseModalOpen(true);
    setOpenDropdownId(null);
  };

  const handlePayRelease = async () => {
    if (!salaryReleaseEmployee) return;
    try {
      await updateBatchEmployeeApi(
        url,
        {
          id: salaryReleaseEmployee.id,
          batchId: salaryReleaseEmployee.batchId,
          employeeId: salaryReleaseEmployee.employeeId,
          hourlyRate: salaryReleaseEmployee.hourlyRate,
          startTime: salaryReleaseEmployee.startTime,
          endTime: salaryReleaseEmployee.endtime,
          startDate: salaryReleaseEmployee.startDate,
          endDate: salaryReleaseEndDate || salaryReleaseEmployee.endDate,
          salary: Number(customSalary),
          status: "Ready for Salary"
        }
      );
      toast.success("Salary and status updated!");
      setSalaryReleaseModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to update salary/status.");
    }
  };

  const handleExtend = async () => {
    if (!salaryReleaseEmployee || !salaryReleaseEmployee.endDate) return;

    try {
      // 1. Update current employee's salary and status
      await updateBatchEmployeeApi(
        url,
        {
          id: salaryReleaseEmployee.id,
          batchId: salaryReleaseEmployee.batchId,
          employeeId: salaryReleaseEmployee.employeeId,
          hourlyRate: salaryReleaseEmployee.hourlyRate,
          startTime: salaryReleaseEmployee.startTime,
          endTime: salaryReleaseEmployee.endtime,
          startDate: salaryReleaseEmployee.startDate,
          endDate: salaryReleaseEndDate || salaryReleaseEmployee.endDate,
          salary: Number(customSalary),
          status: "Ready for Salary"
        }
      );
      // 2. Create new entry for next period
      const prevEndDate = new Date(salaryReleaseEmployee.endDate);
      prevEndDate.setDate(prevEndDate.getDate() + 1);
      const nextStartDate = prevEndDate.toISOString().split('T')[0];

      await addBatchEmployeeApi(
        url,
        salaryReleaseEmployee.batchId,
        salaryReleaseEmployee.employeeId,
        salaryReleaseEmployee.hourlyRate,
        salaryReleaseEmployee.startTime,
        salaryReleaseEmployee.endtime,
        nextStartDate,
        undefined // No endDate
      );
      toast.success("Batch employee extended and salary/status updated!");
      setSalaryReleaseModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to extend batch employee or update salary/status.");
    }
  };

  function calculateHours(start: string, end: string) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let startMinutes = sh * 60 + sm;
    let endMinutes = eh * 60 + em;
    let diff = endMinutes - startMinutes;
    if (diff < 0) diff += 24 * 60;
    return diff / 60;
  }


  function calculateDays(start: string, end: string) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = endDate.getTime() - startDate.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }


  return (
    <div className="px-2 mt-2">

      {isModalOpen && (
        <DeleteItem onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete} />
      )}
      <div className='flex flex-row items-center '>

        <h2 className="text-3xl font-bold text-blue-600 mb-2">Employees
        </h2>
        <button
          type="button"
          className=" w-fit px-3 py-2 mx-3 tracking-wide text-white capitalize transition-colors duration-300 transform bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-80"
          onClick={() => setAssignModal(true)}
        >
          <FaPlus className="w-3 h-3" />
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearchSubmit} className="w-full md:flex-grow mb-4 mt-6">
        <label
          htmlFor="employee-search"
          className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
        >
          Search
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500 dark:text-gray-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            type="search"
            id="employee-search"
            className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Search by Name, Email"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button
            type="submit"
            className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            Search
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="overflow-x-auto mt-5">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Actions</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Name</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Email</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Mobile</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Gender</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Hourly Rate</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Start Time</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">End Time</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Start Date</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">End Date</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
            {loader ? (
              <tr>
                <td colSpan={8} className="py-3 text-center">
                  <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin mx-auto"></div>
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-3 text-center text-gray-500">No employees found.</td>
              </tr>
            ) : (
              employees.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-300 whitespace-nowrap relative" style={{ width: '48px', overflow: 'visible' }}>
                    <div className="relative inline-block" style={{ width: '32px', minWidth: '32px', overflow: 'visible' }}>
                      <button
                        type="button"
                        className="p-2 rounded-md hover:bg-gray-100"
                        onClick={() => setOpenDropdownId(openDropdownId === item.id ? null : item.id)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3 h-5"
                          viewBox="0 0 448 512"
                        >
                          <path d="M8 256a56 56 0 1 1 112 0A56 56 0 1 1 8 256zm160 0a56 56 0 1 1 112 0 56 56 0 1 1 -112 0zm216-56a56 56 0 1 1 0 112 56 56 0 1 1 0-112z" />
                        </svg>
                      </button>
                      {openDropdownId === item.id && (
                        <div
                          className="absolute left-full top-1/2 -translate-y-1/2 ml-1 w-56 rounded-md bg-white ring-1 ring-black ring-opacity-5 shadow-lg focus:outline-none z-50"
                          style={{ minWidth: '10rem', transform: 'translateY(-72%)' }}
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="menu-button"
                        >
                          <div className="py-1" role="none">
                            <div className="px-3">
                              <button
                                onClick={() => { handleEditClick(item.id); setOpenDropdownId(null); }}
                                className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none flex items-center w-full"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="1.5"
                                  stroke="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                  />
                                </svg>
                                <span className="block px-4 py-2 text-sm text-gray-700">Edit</span>
                              </button>
                            </div>
                            <div className="px-3">
                              <button
                                onClick={() => { handleDeleteClick(item.id); setOpenDropdownId(null); }}
                                className="text-gray-500 transition-colors duration-200 dark:hover:text-red-600 dark:text-gray-300 hover:text-red-500 focus:outline-none flex items-center w-full"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="1.5"
                                  stroke="currentColor"
                                  className="w-5 h-5"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                  />
                                </svg>
                                <span className="block px-4 py-2 text-sm text-gray-700">Delete</span>
                              </button>
                            </div>
                            <div className="px-3">
                              <button
                                onClick={() => { handleSalaryReleaseClick(item); setOpenDropdownId(null); }}
                                className="text-gray-500 transition-colors duration-200 dark:hover:text-red-600 dark:text-gray-300 hover:text-red-500 focus:outline-none flex items-center w-full"
                              >
                                <span className="block px-4 py-2 text-sm text-gray-700">Salary Release</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )} 
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.name}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.email}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.mobile}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">
                    {item.user.gender === 1 ? "Male" : item.user.gender === 2 ? "Female" : "Other"}
                  </td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.hourlyRate}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.startTime}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.endtime}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.startDate}</td>
                  <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.endDate ? item.endDate : "N/A"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
        <button
          disabled={currentPage === 1}
          onClick={handlePreviousPage}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
            />
          </svg>
          <span>Previous</span>
        </button>
        <div className="flex flex-wrap items-center justify-center gap-x-2">
          {[...Array(Math.max(totalPages, 1))].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-2 py-1 text-sm ${currentPage === index + 1
                ? "text-blue-500 bg-blue-100"
                : "text-gray-500 hover:bg-gray-100"
                } rounded-md`}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <button
          disabled={currentPage === totalPages}
          onClick={handleNextPage}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <span>Next</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
            />
          </svg>
        </button>
      </div>

      <SideModal isOpen={assignModal} onClose={() => setAssignModal(false)}>
        <AsignEmployee url={url} batchId={batchId} assignModal={assignModal} setAssignModal={setAssignModal} />
      </SideModal>
      <SideModal isOpen={isFormModalOpen} onClose={() => setIsFormModalOpen(false)}>
        <BatchEmployeeForm url={url} editbatchemployeeId={editbatchemployeeId} onClose={() => setIsFormModalOpen(false)} />
      </SideModal>

      {/* Salary Release Popup Card */}
      <SideModal isOpen={salaryReleaseModalOpen} onClose={() => setSalaryReleaseModalOpen(false)} widthClass="w-full md:w-[400px]">
        {salaryReleaseEmployee && (
          <div className="relative animate-fadeIn">
            <h3 className="text-xl font-bold mb-6 text-blue-700 text-center">Salary Release Details</h3>
            <div className="grid grid-cols-1 gap-3 text-gray-700">
              <div><span className="font-semibold">Name:</span> {salaryReleaseEmployee.user.name}</div>
              <div><span className="font-semibold">Start Time:</span> {salaryReleaseEmployee.startTime}</div>
              <div><span className="font-semibold">End Time:</span> {salaryReleaseEmployee.endtime}</div>
              <div><span className="font-semibold">Hourly Rate:</span> ₹{salaryReleaseEmployee.hourlyRate}</div>
              <div><span className="font-semibold">Start Date:</span> {salaryReleaseEmployee.startDate}</div>
              <div className="flex items-center gap-2">
                <span className="font-semibold">End Date:</span>
                {salaryReleaseEmployee.endDate ? (
                  <span>{salaryReleaseEmployee.endDate}</span>
                ) : (
                  <input 
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={salaryReleaseEndDate || ''}
                    min={salaryReleaseEmployee.startDate}
                    onChange={e => {
                      setSalaryReleaseEndDate(e.target.value);
                      setSalaryReleaseEmployee(prev => prev ? { ...prev, endDate: e.target.value } : prev);
                    }}
                  />
                )}
              </div>
            </div>
            {/* Salary Calculation */}
            <div className="mt-6 p-4  rounded-lg text-blue-900 text-center">
              <div className="mb-1 text-sm">Total Salary:</div>
              <div className="text-2xl font-bold flex items-center justify-center gap-2">
                <span>₹</span>
                <input
                  type="number"
                  className=" px-1 py-1 rounded w-[80%] text-center text-2xl font-bold"
                  value={customSalary}
                  onChange={e => setCustomSalary(e.target.value)}
                  min={0}
                />
              </div>
              <div className="mt-2 text-xs text-gray-500">
                (Days × Hours/Day × Hourly Rate)
              </div>
              <div className="flex gap-2 mt-4 justify-center">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  onClick={handlePayRelease}
                  disabled={!customSalary}
                >
                  Release salary
                </button>
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  onClick={handleExtend}
                  disabled={!salaryReleaseEmployee?.endDate}
                >
                  Extend
                </button>
              </div>
            </div>
          </div>
        )}
      </SideModal>
    </div>
  )
}

export default BatchEmployee;

